// @ts-nocheck
import { SortableHeader } from "@/components/shared/SortableHeader";
import { useState, useMemo, useCallback } from "react";
import { Plus, Trash2, Loader2, Download } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { trpcClient } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DataTableRow,
  DataTableRowCheckbox,
  DataTableSelectAllCheckbox,
} from "@/components/shared/DataTableRow";
import { BulkActionBar, type BulkAction } from "@/components/shared/BulkActionBar";
import { useRowSelection } from "@/hooks/useRowSelection";
import { useRoleAccess } from "@/hooks/useRoleAccess";
import { executeBulkAction } from "@/utils/bulkActionUtils";
import { exportToExcel } from "@/lib/excel.utils";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import {
  ListPageShell,
  useListFilter,
} from "@/components/shared/ListPageShell";
import {
  PageFilterTags,
  type PageFilterTagItem,
} from "@/components/shared/PageFilterTags";
import { useConfirm } from "@/providers/ConfirmProvider";
import { ProductTypeDetailDrawer } from "./ProductTypeDetailDrawer";
import { ProductTypeFormModal } from "./ProductTypeFormModal";
import { ExportImportButtons } from "@/components/shared/ExportImportButtons";
import {
  InlineFilterRow,
  type InlineFilterDefinition,
} from "@/components/shared/InlineFilterRow";
import {
  downloadExcelTemplate as downloadTemplate,
  type ExcelColumnDef,
} from "@/lib/excel.utils";
import { useExcelExport } from "@/hooks/useExcelExport";

const EXPORT_COLUMNS: ExcelColumnDef[] = [
  { key: "code", label: "Mã / Tên Loại SP" },
  { key: "category", label: "Danh Mục" },
  { key: "group", label: "Nhóm" },
  { key: "unit", label: "Đơn Vị Tính" },
];

const FILTER_FIELDS: InlineFilterDefinition[] = [
  { key: "code", placeholder: "Mã / Tên...", type: "text" },
  { key: "category", placeholder: "Danh mục...", type: "text" },
  { key: "group", placeholder: "Nhóm...", type: "text" },
  { key: "unit", placeholder: "Đơn vị...", type: "text" },
  { key: "actions", type: "none" },
];

export default function ProductTypeList() {
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<any>(null);
  const [viewingItem, setViewingItem] = useState<any | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Fetch
  const { data, isLoading, error } = useQuery({
    queryKey: ["product-types"],
    queryFn: async () => await trpcClient.config.getProductTypes.query(),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      setDeletingId(id);
      return await trpcClient.config.deleteProductType.mutate({ id });
    },
    onSuccess: async (_, id) => {
      // 1. Optimistic cache eviction (0ms)
      queryClient.setQueryData(["product-types"], (old: any[] | undefined) =>
        old ? old.filter((item: any) => item.id !== id) : []
      );
      setViewingItem((prev: any) => (prev?.id === id ? null : prev));
      // 2. Synchronize all queries
      await Promise.all([
        queryClient.refetchQueries({ queryKey: ["product-types"] }),
        queryClient.invalidateQueries({ queryKey: ["product-types"] }),
        queryClient.invalidateQueries({ queryKey: ["product-types-lookup"] }),
        queryClient.invalidateQueries({ queryKey: ["config", "productTypes"] }),
        queryClient.invalidateQueries({ queryKey: ["combobox"] }),
      ]);
      toast.success("Xóa loại sản phẩm thành công");
    },
    onError: (e: any) => {
      const raw = e?.message || '';
      if (raw.includes('foreign key') || raw.includes('related') || raw.includes('constraint')) {
        toast.error("Không thể xóa loại sản phẩm này vì đang được sử dụng bởi sản phẩm khác.");
      } else {
        toast.error(e?.message || "Lỗi khi xóa loại sản phẩm. Vui lòng thử lại.");
      }
    },
    onSettled: () => {
      setDeletingId(null);
    },
  });

  const allItems = data || [];
  const [selectedCategories, setSelectedCategories] = useState<string[] | null>(
    null,
  );
  const [showFilter, setShowFilter] = useState(false);
  const [filterValues, setFilterValues] = useState<Record<string, any>>({});

  // Compute category counts for filter tags
  const categoryTags = useMemo<PageFilterTagItem[]>(() => {
    const counts: Record<string, number> = {};
    allItems.forEach((item: any) => {
      const cat = item.category || "Chưa phân loại";
      counts[cat] = (counts[cat] || 0) + 1;
    });
    return Object.entries(counts)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, count]) => ({ key, label: key, count }));
  }, [allItems]);

  const preFilter = useCallback((item: any) => {
    if (selectedCategories && selectedCategories.length > 0) {
      const cat = item.category || "Chưa phân loại";
      if (!selectedCategories.includes(cat)) return false;
    }
    if (filterValues.code && !item.code?.toLowerCase().includes(filterValues.code.toLowerCase())) {
      return false;
    }
    if (filterValues.category && !item.category?.toLowerCase().includes(filterValues.category.toLowerCase())) {
      return false;
    }
    if (filterValues.group && !item.group?.toLowerCase().includes(filterValues.group.toLowerCase())) {
      return false;
    }
    if (filterValues.unit && !item.unit?.toLowerCase().includes(filterValues.unit.toLowerCase())) {
      return false;
    }
    return true;
  }, [selectedCategories, filterValues]);

  const {
    filtered: filteredProductTypes,
    paginatedItems,
    totalItems,
    currentPage,
    onPageChange,
    pageSize,
    onPageSizeChange,
    sortField,
    sortOrder,
    handleSort,
  } = useListFilter({
    data: allItems,
    searchFields: ["code", "category", "group", "unit", "standardProcess"],
    preFilter,
  });

  const handleFilterChange = (key: string, value: any) => {
    setFilterValues((prev) => ({ ...prev, [key]: value }));
    onPageChange(1);
  };

  const handleClearAll = () => {
    setFilterValues({});
    setSelectedCategories(null);
    onPageChange(1);
  };

  const handleCategorySelect = useCallback((key: string) => {
    onPageChange(1);
    if (!key) {
      setSelectedCategories(null);
      return;
    }
    setSelectedCategories((prev) => {
      if (!prev) return [key];
      if (prev.includes(key)) {
        const next = prev.filter((k) => k !== key);
        return next.length === 0 ? null : next;
      }
      return [...prev, key];
    });
  }, [onPageChange]);

  const activeViewingItem = useMemo(() => {
    if (!viewingItem) return null;
    return allItems.find((i: any) => i.id === viewingItem.id) || viewingItem;
  }, [allItems, viewingItem]);

  const { handleExport, isExporting } = useExcelExport({
    filename: "danh_sach_loai_san_pham",
    columns: EXPORT_COLUMNS,
    localData: filteredProductTypes,
  });

  // ── Chọn nhiều dòng + thao tác hàng loạt ──
  const { hasPermission } = useRoleAccess();
  const rowIds = useMemo(() => paginatedItems.map((i: any) => i.id), [paginatedItems]);
  const rowSelection = useRowSelection(rowIds);
  const { selectedCount, selectedIds, clearSelection } = rowSelection;
  const bulkActions: BulkAction[] = selectedCount
    ? [
        {
          icon: <Download className="h-4 w-4" />,
          label: "Xuất Excel",
          variant: "outline",
          onClick: () =>
            exportToExcel(
              filteredProductTypes.filter((i: any) => selectedIds.has(i.id)),
              EXPORT_COLUMNS,
              "loai_san_pham_da_chon",
            ),
        },
        ...(hasPermission("product-types", "delete")
          ? [
              {
                icon: <Trash2 className="h-4 w-4" />,
                label: `Xoá (${selectedCount})`,
                variant: "destructive" as const,
                onClick: () =>
                  executeBulkAction({
                    confirmFn: confirm,
                    selectedIds: Array.from(selectedIds),
                    actionFn: async (id) => trpcClient.config.deleteProductType.mutate({ id }),
                    actionName: "xóa",
                    itemType: "loại sản phẩm",
                    onSuccess: () => {
                      clearSelection();
                      queryClient.invalidateQueries({ queryKey: ["product-types"] });
                      queryClient.invalidateQueries({ queryKey: ["combobox"] });
                    },
                    requireConfirm: true,
                    confirmMessage: `Xóa ${selectedCount} loại sản phẩm đã chọn?`,
                  }),
              },
            ]
          : []),
      ]
    : [];

  if (isLoading)
    return (
      <Card className="p-8 flex items-center justify-center">
        <Loader2 className="animate-spin text-indigo-600 mr-2" /> Đang tải...
      </Card>
    );
  if (error)
    return (
      <Card className="p-8 text-center text-red-500">Lỗi khi tải dữ liệu.</Card>
    );

  return (
    <ListPageShell
        trashModule="product-types"
      unitLabel="loại sản phẩm"
      filterFields={FILTER_FIELDS}
      filterValues={filterValues}
      showFilter={showFilter}
      onToggleFilter={() => setShowFilter((prev) => !prev)}
      onClearAll={handleClearAll}
      headerFilters={
        <PageFilterTags
          items={categoryTags}
          selectedKeys={selectedCategories}
          onSelect={handleCategorySelect}
          hideEmpty={false}
          dropdownLabel="Danh mục"
        />
      }
      actions={
        <ExportImportButtons
          onExport={handleExport}
          exportLoading={isExporting}
          onDownloadTemplate={() => downloadTemplate(EXPORT_COLUMNS, "mau_loai_san_pham")}
        >
          <Button
            size="sm"
            className="h-8 text-xs bg-indigo-600 hover:bg-indigo-700 text-white font-medium"
            onClick={() => {
              setEditingItem(null);
              setIsModalOpen(true);
            }}
          >
            <Plus className="w-3.5 h-3.5 mr-1" /> Thêm mới
          </Button>
        </ExportImportButtons>
      }
      currentPage={currentPage}
      pageSize={pageSize}
      totalItems={totalItems}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
    >
      <Card className="p-0 border-slate-200 shadow-sm overflow-hidden bg-white">
        <Table className={rowSelection.selectionMode ? "apv-select-mode" : undefined}>
          <TableHeader className="bg-slate-50/50">
            <TableRow>
              <DataTableSelectAllCheckbox rowSelection={rowSelection} />
              <SortableHeader
                label="Mã / Tên"
                field="code"
                currentField={sortField}
                currentOrder={sortOrder}
                onSort={handleSort}
                className="apv-col-code w-[150px] font-semibold text-xs py-3"
              />
              <SortableHeader
                label="Danh mục"
                field="category"
                currentField={sortField}
                currentOrder={sortOrder}
                onSort={handleSort}
                className="font-semibold text-xs py-3"
              />
              <SortableHeader
                label="Nhóm"
                field="group"
                currentField={sortField}
                currentOrder={sortOrder}
                onSort={handleSort}
                className="font-semibold text-xs py-3"
              />
              <SortableHeader
                label="Đơn vị"
                field="unit"
                currentField={sortField}
                currentOrder={sortOrder}
                onSort={handleSort}
                className="font-semibold text-xs py-3"
              />
              <TableHead className="apv-col-actions w-[100px] text-right font-semibold text-xs py-3">
                Thao tác
              </TableHead>
            </TableRow>
            {showFilter && (
              <InlineFilterRow
                columns={[
                  null,
                  { key: 'code' },
                  { key: 'category' },
                  { key: 'group' },
                  { key: 'unit' },
                  null,
                ]}
                fields={FILTER_FIELDS}
                values={filterValues}
                onChange={handleFilterChange}
              />
            )}
          </TableHeader>
          <TableBody>
            {paginatedItems.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={6}
                  className="text-center py-8 text-slate-500"
                >
                  Chưa có dữ liệu.
                </TableCell>
              </TableRow>
            ) : (
              paginatedItems.map((item: any) => (
                <DataTableRow
                  key={item.id}
                  id={item.id}
                  rowSelection={rowSelection}
                  className="font-medium cursor-pointer hover:bg-slate-50 transition-colors"
                  onClick={() => setViewingItem(item)}
                >
                  <DataTableRowCheckbox id={item.id} rowSelection={rowSelection} />
                  <TableCell className="apv-col-code font-semibold text-indigo-700 py-2">
                    {item.code}
                  </TableCell>
                  <TableCell className="text-slate-600 py-2">
                    {item.category || "—"}
                  </TableCell>
                  <TableCell className="text-slate-600 py-2">
                    {item.group || "—"}
                  </TableCell>
                  <TableCell className="text-slate-600 py-2">
                    {item.unit || "—"}
                  </TableCell>
                  <TableCell
                    className="apv-col-actions text-right py-2"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        disabled={deletingId === item.id}
                        className="h-7 w-7 hover:text-red-600 focus:text-red-600 disabled:opacity-50"
                        onClick={async () => {
                          if (await confirm(`Xóa ${item.code}?`))
                            deleteMutation.mutate(item.id);
                        }}
                      >
                        {deletingId === item.id ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin text-red-500" />
                        ) : (
                          <Trash2 className="w-3.5 h-3.5 text-red-500" />
                        )}
                      </Button>
                    </div>
                  </TableCell>
                </DataTableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      <ProductTypeDetailDrawer
        item={activeViewingItem}
        open={!!activeViewingItem}
        onOpenChange={(open) => !open && setViewingItem(null)}
        onEdit={(item) => {
          setEditingItem(item);
          setIsModalOpen(true);
        }}
      />

      {isModalOpen && (
        <ProductTypeFormModal
          open={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setEditingItem(null);
          }}
          onSuccess={() => {
            onPageChange(1);
          }}
          id={editingItem?.id}
          initialData={editingItem}
          mode={editingItem ? 'edit' : 'create'}
          breadcrumb={['Loại sản phẩm', editingItem ? 'Cập nhật' : 'Thêm mới']}
        />
      )}
      <BulkActionBar
        selectedCount={selectedCount}
        onClearSelection={clearSelection}
        actions={bulkActions}
      />
    </ListPageShell>
  );
}
